import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { Component, inject, OnInit } from '@angular/core';
import { MatError, MatFormField, MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatLabel } from '@angular/material/form-field';
import { ProductEntity } from '../product.entity';
import { Store } from '@ngxs/store';
import { PixlEditorComponent } from '@wdidev/pixl-editor';
import { environment } from '../../../environments/environment';
import { AuthState } from '../../core/authentication/auth.state';
import { ProductEntityService } from '../services/product.entity.service';
import { WidgetMessageService } from '../../core/services/widget-message.service';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'pixl-form-dialog-product',
  templateUrl: './form-dialog-product.component.html',
  imports: [
    MatDialogContent,
    MatFormField,
    MatDialogTitle,
    MatLabel,
    MatInput,
    MatDialogActions,
    ReactiveFormsModule,
    MatButton,
    PixlEditorComponent,
    MatError,
    MatDividerModule,
    MatTabsModule,
    MatCheckboxModule,
    TranslocoModule,
  ],
  styleUrls: ['./form-dialog-product.component.scss'],
})
export class FormDialogProductComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<FormDialogProductComponent>>(MatDialogRef);

  data? = inject<Partial<ProductEntity>>(MAT_DIALOG_DATA);

  form: FormGroup;

  private store = inject(Store);

  currentPage = 1;
  pageSize = 20;

  // pixl editor bindings
  public authToken: string | null = '';
  public assetsUrl: string = environment.apiUrl + '/assets';
  public mediaUrl: string = environment.assetsHost + '/';

  constructor() {
    const data = this.data;

    this.form = this.fb.group({
      name: [data?.name || ''],
      isbn: [data?.isbn || ''],
      imprint: [data?.imprint || ''],
      imprintActive: [data?.imprintActive ?? false],
      updatedAt: [data?.updatedAt || ''],
      changedBy: [data?.changedBy || ''],
    });
  }

  async ngOnInit() {
    // bearer token for Pixl Editor
    this.authToken = this.store.selectSnapshot(AuthState.getToken);
  }

  private productService = inject(ProductEntityService);
  private widgetMessageService = inject(WidgetMessageService);
  private translocoService = inject(TranslocoService);

  async save(): Promise<void> {
    if (!this.form.valid) {
      return;
    }

    const formValue = this.form.value as Partial<ProductEntity>;
    const isEdit = !!this.data?.id;

    const entity = Object.assign(
      new ProductEntity(),
      { id: this.data?.id },
      formValue,
    );

    try {
      if (isEdit) {
        await firstValueFrom(
          this.productService.update(entity as ProductEntity),
        );
      }
      else {
        await this.productService.create(entity);
      }
      // close only on success
      this.dialogRef.close(entity);
    }
    catch (e) {
      // handle JSON:API validation error for unique ISBN
      const err = e as HttpErrorResponse;
      if (err?.status === 422) {
        const errorBody = err.error as { errors?: { source?: { pointer?: string }, detail?: string }[] };
        const errors = (errorBody && errorBody.errors) ? errorBody.errors : null;
        if (Array.isArray(errors)) {
          const isbnErrors: Record<string, boolean> = {};
          for (const er of errors) {
            if (er?.source?.pointer === '/data/attributes/isbn') {
              if (er?.detail === 'validation.unique') {
                isbnErrors.unique = true;
              }
              if (er?.detail === 'validation.regex') {
                isbnErrors.regex = true;
              }
            }
          }
          if (Object.keys(isbnErrors).length > 0) {
            this.form.get('isbn')?.setErrors(isbnErrors);
            this.form.get('isbn')?.markAsTouched();
            // show a user-friendly toast instead of the raw backend error
            this.widgetMessageService.widgetShowMessage = false;
            this.widgetMessageService.setMessage({
              message: this.translocoService.translate(
                'product.form_has_errors',
              ),
              messageTye: 'mtError',
            });
            // keep the dialog open
            return;
          }
        }
      }
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
